// React & redux
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DelimitedArrayParam, StringParam, useQueryParams } from 'use-query-params';

// Hooks
import { useTranslation } from 'react-i18next';

// Utils
import { convertDateToString, convertMilliSecondToDate, getLastMonth } from '../../utils/Date.Util';
import {
  generateChargerStatusHistory,
  generateFullAddress,
  getChargerStatus,
  getSortedRecentSessions,
} from './utils';

// Components
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  CustomDatePicker,
  Dropdown,
  DropdownType,
  Label,
  LabelType,
  MODAL_TYPES,
  Pill,
  useGlobalModalContext,
} from '../_ui';
import { SessionDetail } from './SessionDetail.component';
import { SessionTable } from './SessionTable.component';

// API
import {
  useFetchRecentTransactionsQuery,
  useLazyFetchTransactionReportCSVQuery,
} from '../../services/history.api';
import { SessionOptions, useLazyFetchSessionsQuery } from '../../services/session.api';

// Assets
import { SortingState } from '@tanstack/react-table';
import { NUMBER } from '../../constant/Number.constant';
import { useCompany } from '../../hooks/useCompany';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useLazyFetchChargersQuery } from '../../services/charger.api';
import { SCOPE_TYPE } from '../../services/utils';
import { Charger } from '../../stores/types';
import { DATE_RANGE } from '../_ui/date-picker/Custom-Date-Picker.component';

interface SessionsProps {
  tableId: string;
  enableFilterCharger?: boolean;
  enableExportCSV?: boolean;
  dataMap?: Array<string>;
  vehicleId?: string;
  locationId?: string;
  chargerId?: string;
  showTitle?: boolean;
  cardPadding?: string;
  companyId: string;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  isLocalTimezone?: boolean;
  setIsLocalTimezone?: (selected: boolean) => void;
}

export const Sessions = ({
  tableId,
  locationId,
  enableFilterCharger = true,
  enableExportCSV = true,
  dataMap,
  vehicleId,
  chargerId,
  showTitle = true,
  cardPadding,
  companyId,
  currentPage: controlledCurrentPage,
  setCurrentPage: controlledSetCurrentPage,
  isLocalTimezone: controlledIsLocalTimezone,
  setIsLocalTimezone: controlledSetIsLocalTimezone,
}: SessionsProps) => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const [triggerFetchChargers, { data }] = useLazyFetchChargersQuery();
  const { LAST_WEEK, LAST_MONTH, CUSTOM_RANGE } = DATE_RANGE;
  const { isMultiViewActive } = useCompany();

  const chargersGroupByLocation = _.groupBy(data?.entities, 'location.name');
  const ChargersWithLocation = Object.entries(chargersGroupByLocation)
    .map((item) => ({
      ...item,
      chargers: item[1],
    }))
    .filter((item) => item.chargers.length > NUMBER.ZERO);

  const defaultChargers = useMemo(() => {
    return data?.entities.map((charger) => ({
      id: charger.id,
      label: charger.name || '',
    }));
  }, [data]);

  const [sessionQuery, setSessionQuery] = useQueryParams({
    start: StringParam,
    end: StringParam,
    option: StringParam,
    chargerId: DelimitedArrayParam,
  });

  // Refetch & get date range from url (if present) else default value (i.e. last month) when url query changes
  const dateRange = useMemo(() => {
    const lastMonth = getLastMonth();
    return [
      sessionQuery.start ? convertMilliSecondToDate(sessionQuery.start) : lastMonth[0],
      sessionQuery.end ? convertMilliSecondToDate(sessionQuery.end) : lastMonth[1],
    ];
  }, [sessionQuery]);

  // if location change && enable dropdown, we need to refetch
  useEffect(() => {
    if (enableFilterCharger) {
      triggerFetchChargers({
        scope: SCOPE_TYPE.COMPANY,
        'filter_eq[locationId]': locationId,
        companyId: companyId,
      });
    }
  }, [companyId, enableFilterCharger, locationId, triggerFetchChargers]);

  // Chargers get updated when either defaultchargers changes or charger id in search param gets changed
  const chargers = useMemo(() => {
    return (
      defaultChargers?.map((charger) => ({
        ...charger,
        selected: sessionQuery.chargerId?.includes(charger.id),
      })) || []
    );
  }, [defaultChargers, sessionQuery.chargerId]);

  const chargersFilterDropdownList = useMemo(() => {
    return ChargersWithLocation.map((key: any) => {
      const children = key.chargers.map((charger: Charger) => {
        return {
          id: charger.id,
          label: charger.name,
          selected: sessionQuery.chargerId?.includes(charger.id as string),
        };
      });

      return {
        id: key[0],
        label: key[0],
        selected: children?.every((child: any) => child.selected) || false,
        children: children || [],
      };
    });
  }, [sessionQuery, ChargersWithLocation]);

  const isAnyChargerSelected = useMemo(() => {
    return chargers.some((charger) => charger.selected);
  }, [chargers]);

  // combine all data to SessionOptions for API
  // if no chargers select, we only send location id.
  const filter = useMemo(() => {
    return {
      sort: 'desc',
      dateRange,
      filter_hasCompletedTx: false,
      vehicleId,
      locationId,
      chargers: enableFilterCharger
        ? chargers
        : vehicleId
        ? []
        : [{ id: chargerId, selected: true }],
      scope: isMultiViewActive ? SCOPE_TYPE.ALL : SCOPE_TYPE.COMPANY,
      companyId,
    } as SessionOptions;
  }, [
    dateRange,
    vehicleId,
    locationId,
    enableFilterCharger,
    chargers,
    chargerId,
    companyId,
    isMultiViewActive,
  ]);

  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalIsLocalTimezone, setInternalIsLocalTimezone] = useState(true);

  const currentPage = controlledCurrentPage ?? internalCurrentPage;
  const setCurrentPage = controlledSetCurrentPage ?? setInternalCurrentPage;

  const isLocalTimezone = controlledIsLocalTimezone ?? internalIsLocalTimezone;
  const setIsLocalTimezone = controlledSetIsLocalTimezone ?? setInternalIsLocalTimezone;

  const [triggerFetchSessions, { data: sessionData, isLoading: isSessionLoading }] =
    useLazyFetchSessionsQuery();
  useEffect(() => {
    if (filter.companyId || chargerId) {
      triggerFetchSessions(filter);
    }
  }, [filter, chargerId, triggerFetchSessions]);

  const { transactions, isTransactionLoading } = useFetchRecentTransactionsQuery(filter, {
    selectFromResult: (endPoint) => ({
      transactions: endPoint.data ? endPoint.data.entities : [],
      isTransactionLoading: endPoint.isFetching,
    }),
  });

  const [triggerFetchReportCSV] = useLazyFetchTransactionReportCSVQuery();

  const checkIsSessionLoading = useMemo(() => {
    return isSessionLoading || isTransactionLoading;
  }, [isSessionLoading, isTransactionLoading]);

  const [sorting, setSorting] = useLocalStorageState<SortingState>(`${tableId}-sortConfig`, []);

  useEffect(() => {
    // if any filter changes, back to page 1
    setCurrentPage(1);
  }, [filter]);

  const handleClearAllClick = () => {
    setSessionQuery({ chargerId: undefined });
  };

  const renderClearAllButton = () => {
    if (isAnyChargerSelected) {
      return (
        <button type='button' className='pl-2 text-left' onClick={handleClearAllClick}>
          <Label
            type={LabelType.DROPDOWN_ITEM_SELECTED}
            color={ColorType.DENIM}
            text={t('session_clear_all')}
          />
        </button>
      );
    }

    return null;
  };
  const selectedFilter = useMemo(() => {
    return sessionQuery.chargerId && sessionQuery.chargerId.length > 0
      ? sessionQuery.chargerId.map((item) => {
          return { type: 'CHARGER', id: item };
        })
      : [];
  }, [sessionQuery]);

  const handleFilterItemClick = (item: any) => {
    if (item.type === 'CHARGER') {
      const filterCharger = [...selectedFilter].filter((i) => i.id !== item.id);
      setSessionQuery({
        chargerId:
          filterCharger?.length > 0 ? filterCharger.map((charger) => charger.id) : undefined,
      });
    } else {
      const filteredChargersList: any =
        chargersFilterDropdownList.find((ele: any) => ele.id === item.id)?.children || [];
      const filteredChargersListIds: string[] = filteredChargersList.map((ele: any) => ele.id);
      const filterCharge = [...selectedFilter].filter((i) => {
        return !filteredChargersListIds.includes(i.id || '');
      });
      setSessionQuery({
        chargerId: filterCharge?.length > 0 ? filterCharge.map((charger) => charger.id) : undefined,
      });
    }
  };

  const renderSelectedCharger = () => {
    if (selectedFilter.length === NUMBER.ZERO) {
      return <div />;
    }
    const chargerPillsToShow: any[] = [];
    chargersFilterDropdownList.forEach((ele) => {
      if (ele.selected) {
        chargerPillsToShow.push({
          id: ele.id,
          label: ele.label,
          type: 'LOCATION',
        });
      } else {
        ele.children.forEach((child: any) => {
          if (child.selected) {
            chargerPillsToShow.push({
              id: child.id,
              label: child.label,
              type: 'CHARGER',
            });
          }
        });
      }
    });
    return (
      <>
        {chargerPillsToShow.map((item) => {
          return (
            <Pill
              key={item.id}
              label={item.label}
              hasCloseButton
              onClick={() => handleFilterItemClick(item)}
              translationOn
            />
          );
        })}
      </>
    );
  };

  const handleChargersFilterChange = (items: any) => {
    const existingSelectedFilter = [...selectedFilter];
    const selectedChargerFilters: any[] = [];

    items.forEach((item: any) => {
      item.children.forEach((child: any) => {
        const filterObj = { type: 'CHARGER', id: child.id };
        if (child.selected) {
          if (!_.some(selectedFilter, filterObj)) {
            selectedChargerFilters.push(filterObj);
          }
        } else if (_.some(selectedFilter, filterObj)) {
          _.remove(existingSelectedFilter, { id: child.id });
        }
      });
    });

    setSessionQuery({
      chargerId: [...existingSelectedFilter, ...selectedChargerFilters].map((ele) => ele.id),
    });
  };

  const handleToggleChange = (selected: boolean) => {
    setIsLocalTimezone(selected);
  };
  const renderSessionDetail = useCallback(
    (rowData: any, islocal: boolean) => {
      const SessionDetailInfo = {
        id: rowData.id,
        startTime: rowData.startTime || rowData.createTime,
        endTime: rowData.stopTime || rowData.completeTime,
        timeZone: islocal
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : rowData.port?.charger?.location?.timeZone,
        authenticationType: rowData?.startedVia,
        vehicleName: rowData.vehicleName,
        isVehicleActive: rowData.vehicleIdActive,
        rfids: rowData.rfids,
        chargerId: rowData.port?.charger?.id,
        charger: rowData.port?.charger?.name,
        connector: rowData.port?.connectorType,
        connectorSide: t(rowData.port?.physicalLocation.toLowerCase()),
        connectorUrl: 'N/A',
        location: rowData.port?.charger?.location?.name,
        address: generateFullAddress(rowData.port?.charger?.location),
        kwhUsed: rowData.consumedEnergyKwh,
        cost: rowData.billedTotalAmount,
        currency: rowData.billedCurrency,
        sessionStatus: rowData.status,
        userEmail: rowData.userEmail,
        statusHistory: generateChargerStatusHistory(
          getChargerStatus(rowData),
          rowData.startTime || rowData.createTime,
          rowData.stopTime || rowData.completeTime,
          t,
          islocal
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : rowData.port?.charger?.location?.timeZone,
        ),
        receiptId: rowData.receiptId,
        paymentTerminalSerialNumber: rowData.paymentTerminalSerialNumber,
        paymentTerminalSessionId: rowData.paymentTerminalSessionId,
      };
      return <SessionDetail sessionData={SessionDetailInfo} />;
    },
    [t],
  );

  const rowClick = useCallback(
    (rowData: any) => {
      showModal(MODAL_TYPES.INFO_MODAL, {
        title: t('session_detail'),
        height: 'max-content',
        onRenderBody: () => renderSessionDetail(rowData, isLocalTimezone),
      });
    },
    [isLocalTimezone, renderSessionDetail, showModal, t],
  );

  const handleButtonClick = () => {
    const { sort, ...rest } = filter;
    triggerFetchReportCSV(rest);
  };

  const renderSelectedChargers = () => {
    if (isAnyChargerSelected) {
      return (
        <div className='gap-2 mt-5 inline-flex flex-wrap'>
          {renderSelectedCharger()}
          {renderClearAllButton()}
        </div>
      );
    }
    return null;
  };

  const recentSessions = useMemo(() => {
    return getSortedRecentSessions(sessionData?.entities || [], transactions, chargers, dateRange);
  }, [sessionData?.entities, transactions, chargers, dateRange]);

  return (
    <Card className={cardPadding} title={showTitle ? t('session_recent_sessions') : ''}>
      <div className='flex mt-3 w-full'>
        <div className='flex w-4/5'>
          {enableFilterCharger && (
            <div className='mr-2'>
              <Dropdown
                placeholder={t('charger')}
                placeholderLabelType={LabelType.SESSION_DROPDOWN_HEADER}
                headerWidth='auto'
                type={DropdownType.CHECKBOX_TREE}
                items={chargersFilterDropdownList}
                onItemClick={handleChargersFilterChange}
                showFooter
                dataTestId='session-select-charger'
              />
            </div>
          )}
          <CustomDatePicker
            optionList={[LAST_WEEK, LAST_MONTH, CUSTOM_RANGE]}
            format='MMM d,yyyy'
            onChange={(option, range) => {
              setSessionQuery({
                option,
                start: convertDateToString(range[0]),
                end: convertDateToString(range[1]),
              });
            }}
            selectedOption={sessionQuery.option || DATE_RANGE.LAST_MONTH}
            selectedDate={
              sessionQuery.start && sessionQuery.end
                ? [
                    convertMilliSecondToDate(sessionQuery.start),
                    convertMilliSecondToDate(sessionQuery.end),
                  ]
                : []
            }
            showHighligtHeader={false}
            dataTestId='session-select-date-range'
          />
        </div>
        {enableExportCSV && (
          <div className='flex justify-end w-1/5'>
            <Button
              size={ButtonSize.SMALL}
              label={t('export_csv')}
              type={ButtonType.TERTIARY}
              onClick={handleButtonClick}
              dataTestId='session-select-export-csv'
            />
          </div>
        )}
      </div>

      {enableFilterCharger && renderSelectedChargers()}
      <SessionTable
        data={recentSessions}
        isLoading={checkIsSessionLoading}
        currentPage={currentPage}
        onLoadPage={(page) => setCurrentPage(page)}
        sorting={sorting}
        setSorting={setSorting}
        dataMap={dataMap}
        onRowClick={rowClick}
        isLocalTimezone={isLocalTimezone}
        onToggleTimezone={handleToggleChange}
        t={t}
      />
    </Card>
  );
};
